import React, { memo, useState, useEffect, useCallback } from "react";
import PropTypes from "prop-types";
import AOS from "aos/dist/aos";
import withStyles from "@mui/styles/withStyles";
import NavBar from "./navigation/NavBar";
import Footer from "./footer/Footer";
import "aos/dist/aos.css";
import dummyBlogPosts from "../content_data/blogPosts";
import dummyVideoPosts from "../content_data/videoPosts";
import DialogSelector from "./register_login/DialogSelector";
import Routing from "./Routing";
import smoothScrollTop from "../../shared/functions/smoothScrollTop";
import axios from "axios";
import { set } from "date-fns";
import jwtDecode from "jwt-decode";
import { useTheme } from "@mui/styles";
import { useLocation } from "react-router-dom";
import { Close } from "@mui/icons-material";

AOS.init({ once: true });

const styles = (theme) => ({
  wrapper: {
    backgroundColor: theme.palette.mode === "light" ? "#FFF" : "#000",
    overflowX: "hidden",
  },
});

function Main(props) {
  const { classes, toggleDarkMode, themeMode } = props;
  const [selectedTab, setSelectedTab] = useState(null);
  const [isMobileDrawerOpen, setIsMobileDrawerOpen] = useState(false);
  const [blogPosts, setBlogPosts] = useState([]);
  const [videoPosts, setVideoPosts] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(null);
  const [openCreatePost, setOpenCreatePost] = useState(false);
  const [userLoggedIn, setUserLoggedIn] = useState(false);
  const [showBrowserBanner, setShowBrowserBanner] = useState(false);

  const theme = useTheme();
  const location = useLocation();

  // Check if current page is password reset or legal pages
  const isPasswordResetPage = location.pathname.includes('/password-reset');
  const isLegalPage = location.pathname.includes('/privacy-policy') || location.pathname.includes('/terms-of-service');

  useEffect(() => {
    AOS.init();
  }, [theme]);

  useEffect(() => {
    const ua = navigator.userAgent;
    const isDesktop = !/Mobi|Android|iPhone|iPad|Tablet/i.test(ua);
    const isChrome = /Chrome\//.test(ua) && !/Edg\//.test(ua) && !/OPR\//.test(ua);
    if (isDesktop && !isChrome) {
      setShowBrowserBanner(true);
    }
  }, []);

  const selectHome = useCallback(() => {
    smoothScrollTop();
    document.title = "Auralyze.ai";
    setSelectedTab("Home");
  }, [setSelectedTab]);

  const selectBlog = useCallback(() => {
    smoothScrollTop();
    document.title = "Auralyze.ai- Blog";
    setSelectedTab("Blog");
  }, [setSelectedTab]);

  const selectVideo = useCallback(() => {
    smoothScrollTop();
    document.title = "Auralyze.ai- Videos";
    setSelectedTab("Videos");
  }, [setSelectedTab]);

  const selectPricing = useCallback(() => {
    smoothScrollTop();
    document.title = "Auralyze.ai- Pricing";
    setSelectedTab("Pricing");
  }, [setSelectedTab]);

  const selectFaq = useCallback(() => {
    smoothScrollTop();
    document.title = "Auralyze.ai- Faqs";
    setSelectedTab("FAQs");
  }, [setSelectedTab]);

  const selectCommunity = useCallback(() => {
    smoothScrollTop();
    document.title = "Auralyze.ai- Community";
    setSelectedTab("Community");
  }, [setSelectedTab]);

  const openLoginDialog = useCallback(() => {
    setDialogOpen("login");
    setIsMobileDrawerOpen(false);
  }, [setDialogOpen, setIsMobileDrawerOpen]);

  const closeDialog = useCallback(() => {
    setDialogOpen(null);
  }, [setDialogOpen]);

  const openRegisterDialog = useCallback(() => {
    setDialogOpen("register");
    setIsMobileDrawerOpen(false);
  }, [setDialogOpen, setIsMobileDrawerOpen]);

  const openTermsDialog = useCallback(() => {
    setDialogOpen("termsOfService");
  }, [setDialogOpen]);

  const handleMobileDrawerOpen = useCallback(() => {
    setIsMobileDrawerOpen(true);
  }, [setIsMobileDrawerOpen]);

  const handleMobileDrawerClose = useCallback(() => {
    setIsMobileDrawerOpen(false);
  }, [setIsMobileDrawerOpen]);

  const openChangePasswordDialog = useCallback(() => {
    setDialogOpen("changePassword");
  }, [setDialogOpen]);

  const fetchBlogPosts = useCallback(() => {
    const blogPosts = dummyBlogPosts
      .map((blogPost) => {
        let title = blogPost.title;
        title = title.toLowerCase();
        /* Remove unwanted characters, only accept alphanumeric and space */
        title = title.replace(/[^A-Za-z0-9 ]/g, "");
        /* Replace multi spaces with a single space */
        title = title.replace(/\s{2,}/g, " ");
        /* Replace space with a '-' symbol */
        title = title.replace(/\s/g, "-");
        blogPost.url = `/blog/post/${title}`;
        blogPost.params = `?id=${blogPost.id}`;
        return blogPost;
      })
      .sort((a, b) => b.date - a.date);

    setBlogPosts(blogPosts);
  }, [setBlogPosts]);

  const fetchVideoPosts = useCallback(() => {
    const videoPosts = dummyVideoPosts
      .map((videoPost) => {
        let title = videoPost.title;
        title = title.toLowerCase();
        /* Remove unwanted characters, only accept alphanumeric and space */
        title = title.replace(/[^A-Za-z0-9 ]/g, "");
        /* Replace multi spaces with a single space */
        title = title.replace(/\s{2,}/g, " ");
        /* Replace space with a '-' symbol */
        title = title.replace(/\s/g, "-");
        videoPost.url = `/video/post/${title}`;
        videoPost.params = `?id=${videoPost.id}`;
        return videoPost;
      })
      .sort((a, b) => b.date - a.date);
    setVideoPosts(videoPosts);
  }, [setVideoPosts]);

  useEffect(fetchBlogPosts, [fetchBlogPosts]);
  useEffect(fetchVideoPosts, [fetchVideoPosts]);

  useEffect(() => {
    const checkResume = async () => {
      if (localStorage.getItem("jwtToken")) {
        const token = localStorage.getItem("jwtToken");
        const decoded = await jwtDecode(token);
        setUserLoggedIn(true);
        const res = await axios.post(`/api/users/user-resume`);

        if (res.data.resume) {
          setUserLoggedIn(true);
        }
      }
    };
    checkResume();
  }, []);

  const handleToggleCreatePost = () => {
    setOpenCreatePost(!openCreatePost);
  };

  const Banner = ({ onClose }) => (
    <div
      style={{
        width: '100%',
        background: 'linear-gradient(90deg, #9461FF 0%, #BFA4F8 100%)',
        color: 'white',
        fontWeight: 600,
        fontSize: '0.9rem',
        padding: '10px 16px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        position: 'relative',
        borderRadius: '12px',
        margin: '8px 16px'
      }}
    >
      ⚠️  This application works best on <strong style={{margin: '0 4px'}}>Google Chrome</strong>. Please switch browsers for full functionality.
      {onClose && (
        <Close
          onClick={onClose}
          style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', cursor: 'pointer' }}
        />
      )}
    </div>
  );

  return (
    <div className={classes.wrapper}>
      <DialogSelector
        openLoginDialog={openLoginDialog}
        dialogOpen={dialogOpen}
        onClose={closeDialog}
        openTermsDialog={openTermsDialog}
        openRegisterDialog={openRegisterDialog}
        openChangePasswordDialog={openChangePasswordDialog}
      />
      {!isPasswordResetPage && !isLegalPage && (
        <NavBar
          selectedTab={selectedTab}
          selectTab={setSelectedTab}
          openLoginDialog={openLoginDialog}
          mobileDrawerOpen={isMobileDrawerOpen}
          handleMobileDrawerOpen={handleMobileDrawerOpen}
          handleMobileDrawerClose={handleMobileDrawerClose}
          userLoggedIn={userLoggedIn}
          toggleDarkMode={toggleDarkMode}
          themeMode={themeMode}
        />
      )}
      {showBrowserBanner && <Banner onClose={() => setShowBrowserBanner(false)} />}
      <Routing
        blogPosts={blogPosts}
        videoPosts={videoPosts}
        selectHome={selectHome}
        selectBlog={selectBlog}
        selectVideo={selectVideo}
        selectFaq={selectFaq}
        selectPricing={selectPricing}
        userLoggedIn={userLoggedIn}
        openLoginDialog={openLoginDialog}
        selectCommunity={selectCommunity}
        openCreatePost={openCreatePost}
        handleToggleCreatePost={handleToggleCreatePost}
        themeMode={themeMode}
      />
      {!isLegalPage && <Footer />}
    </div>
  );
}

Main.propTypes = {
  classes: PropTypes.object.isRequired,
};

export default withStyles(styles, { withTheme: true })(memo(Main));
